import { useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// components
import usePermission from 'src/permission/has-permission';
import { DOCUMENTATION, SUPPORT } from 'src/permission/all_permissions';
import Iconify from 'src/components/iconify';
import { doc } from 'firebase/firestore';
// ----------------------------------------------------------------------

const icon = (
  name: string // <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{width: 1, height: 1}}/>
) => (
  // OR
  <Iconify icon={name} />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('mdi:account'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  salesforce: icon('mdi:salesforce'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  library: icon('mdi:library'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  job: icon('iconamoon:synchronize-duotone'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  account: icon('solar:home-bold-duotone'),
  credential: icon('mdi:cloud-key'),
  approval: icon('wpf:approval'),
  security: icon('wpf:key-security'),
  dict: icon('streamline:dictionary-language-book-solid'),
  translation: icon('dashicons:translation'),
  faqs: icon('eos-icons:workload'),
  support: icon('bx:support'),
  billing: icon('material-symbols:payments'),
};

const GetPermissions = () => ({
  docsUserRoles: usePermission(DOCUMENTATION.VIEW_USER_DOCUMENTATION),
  docsRegisterUser: usePermission(DOCUMENTATION.VIEW_USER_DOCUMENTATION),
  docsInviteUser: usePermission(DOCUMENTATION.VIEW_USER_DOCUMENTATION),
  docsPasswordReset: usePermission(DOCUMENTATION.VIEW_USER_DOCUMENTATION),
  docsTwoFactorAuthentications: usePermission(DOCUMENTATION.VIEW_USER_DOCUMENTATION),

  docsSetupApproval: usePermission(DOCUMENTATION.VIEW_MTJOB_DOCUMENTATION),
  docsOrgCredentials: usePermission(DOCUMENTATION.VIEW_TANSLATE_SETUP_DOCUMENTATION),
  docsTranslationEngine: usePermission(DOCUMENTATION.VIEW_TANSLATE_SETUP_DOCUMENTATION),
  docsGlossary: usePermission(DOCUMENTATION.VIEW_TANSLATE_SETUP_DOCUMENTATION),
  docsNotification: usePermission(DOCUMENTATION.VIEW_TANSLATE_SETUP_DOCUMENTATION),

  docsRetrieveMetadata: usePermission(DOCUMENTATION.VIEW_META_TRANSLATOR_DOCUMENTATION),
  docsFilterMetadata: usePermission(DOCUMENTATION.VIEW_META_TRANSLATOR_DOCUMENTATION),
  docsTranslateMetadata: usePermission(DOCUMENTATION.VIEW_META_TRANSLATOR_DOCUMENTATION),
  docsDeployMetadata: usePermission(DOCUMENTATION.VIEW_META_TRANSLATOR_DOCUMENTATION),
  docsLimit: usePermission(DOCUMENTATION.VIEW_TANSLATE_SETUP_DOCUMENTATION),
  docsTokenHistory: usePermission(DOCUMENTATION.VIEW_TANSLATE_SETUP_DOCUMENTATION),

  docsCreateUpdateJob: usePermission(DOCUMENTATION.VIEW_MTJOB_DOCUMENTATION),
  docsMTJobStatus: usePermission(DOCUMENTATION.VIEW_MTJOB_DOCUMENTATION),
  docsCompareOrgs: usePermission(DOCUMENTATION.VIEW_MTJOB_DOCUMENTATION),
  docsOrgsDeployment: usePermission(DOCUMENTATION.VIEW_MTJOB_DOCUMENTATION),

  docsInstallAPackage: usePermission(DOCUMENTATION.VIEW_SALESFORCE_DOCUMENTATION),
  docsAuthenticationSalesforce: usePermission(DOCUMENTATION.VIEW_SALESFORCE_DOCUMENTATION),
  docsTranslate: usePermission(DOCUMENTATION.VIEW_SALESFORCE_DOCUMENTATION),
  docsDetectLanguage: usePermission(DOCUMENTATION.VIEW_SALESFORCE_DOCUMENTATION),
  docsEmailAI: usePermission(DOCUMENTATION.VIEW_SALESFORCE_DOCUMENTATION),
  docsEditRecordPage: usePermission(DOCUMENTATION.VIEW_SALESFORCE_DOCUMENTATION),
  docsCaseCommentAI: usePermission(DOCUMENTATION.VIEW_SALESFORCE_DOCUMENTATION),
  docsRecordAI: usePermission(DOCUMENTATION.VIEW_SALESFORCE_DOCUMENTATION),
  docsFileAI: usePermission(DOCUMENTATION.VIEW_SALESFORCE_DOCUMENTATION),
  docsAIService: usePermission(DOCUMENTATION.VIEW_SALESFORCE_DOCUMENTATION),
  docsFlowSTAction: usePermission(DOCUMENTATION.VIEW_SALESFORCE_DOCUMENTATION),
  docsSTCiCdComponent: usePermission(DOCUMENTATION.VIEW_EXTENSIONS_DOCUMENTATION),
  docsJsonParser: usePermission(DOCUMENTATION.VIEW_EXTENSIONS_DOCUMENTATION),
  docsAsyncResponseCheck: usePermission(DOCUMENTATION.VIEW_EXTENSIONS_DOCUMENTATION),
  docsN8nSetup: usePermission(DOCUMENTATION.VIEW_EXTENSIONS_DOCUMENTATION),
  docsChatbotAIAgent: usePermission(DOCUMENTATION.VIEW_EXTENSIONS_DOCUMENTATION),
  docsFeedback: usePermission(DOCUMENTATION.VIEW_EXTENSIONS_DOCUMENTATION),

  docsToken: usePermission(DOCUMENTATION.VIEW_OPEN_APIS_DOCUMENTATION),

  docsBilling: usePermission(DOCUMENTATION.VIEW_USER_DOCUMENTATION), // Using USER_DOCUMENTATION for billing access

  allowedFaqs: usePermission(SUPPORT.VIEW_FAQS),
  allowedContact: usePermission(SUPPORT.VIEW_CONTACT),
});

export function useDocsData() {
  const {
    docsUserRoles,
    docsRegisterUser,
    docsInviteUser,
    docsPasswordReset,
    docsTwoFactorAuthentications,

    docsSetupApproval,
    docsOrgCredentials,
    docsTranslationEngine,
    docsGlossary,
    docsNotification,

    docsRetrieveMetadata,
    docsFilterMetadata,
    docsTranslateMetadata,
    docsDeployMetadata,
    docsLimit,
    docsTokenHistory,

    docsCreateUpdateJob,
    docsMTJobStatus,
    docsCompareOrgs,
    docsOrgsDeployment,

    docsInstallAPackage,
    docsAuthenticationSalesforce,
    docsTranslate,
    docsDetectLanguage,
    docsEmailAI,
    docsEditRecordPage,
    docsCaseCommentAI,
    docsRecordAI,
    docsFileAI,
    docsSTCiCdComponent,
    docsAIService,
    docsFlowSTAction,
    docsAsyncResponseCheck,
    docsJsonParser,
    docsN8nSetup,
    docsChatbotAIAgent,
    docsFeedback,

    docsToken,

    docsBilling,

    allowedFaqs,
    allowedContact,
  } = GetPermissions();

  const docsUserItems = useMemo(() => {
    const items = [];

    if (docsUserRoles) {
      items.push({
        title: 'User Roles Blog',
        path: paths.docs.user.roles,
        icon: ICONS.blank,
      });
    }

    if (docsRegisterUser) {
      items.push({
        title: 'Register User Guide',
        path: paths.docs.user.register,
        icon: ICONS.blank,
      });
    }

    if (docsInviteUser) {
      items.push({
        title: 'Invite User Guide',
        path: paths.docs.user.invite,
        icon: ICONS.blank,
      });
    }

    if (docsTwoFactorAuthentications) {
      items.push({
        title: 'Two-Factor Authentication',
        path: paths.docs.user.twoFactorAuthentication,
        icon: ICONS.blank,
      });
    }

    if (docsPasswordReset) {
      items.push({
        title: 'Password Reset Help',
        path: paths.docs.user.passwordReset,
        icon: ICONS.blank,
      });
    }

    return items;
  }, [
    docsUserRoles,
    docsRegisterUser,
    docsInviteUser,
    docsTwoFactorAuthentications,
    docsPasswordReset,
  ]);

  const docsSetupItems = useMemo(() => {
    const items = [];

    if (docsSetupApproval) {
      items.push({
        title: 'Approval Guide',
        path: paths.docs.setup.setupApproval,
        icon: ICONS.blank,
      });
    }
    if (docsOrgCredentials) {
      items.push({
        title: 'Org Credentials Help',
        path: paths.docs.setup.orgCredentials,
        icon: ICONS.blank,
      });
    }

    if (docsTranslationEngine) {
      items.push({
        title: 'Translation Engine Guide',
        path: paths.docs.setup.translationEngine,
        icon: ICONS.blank,
      });
    }

    if (docsGlossary) {
      items.push({
        title: 'Glossary Guide',
        path: paths.docs.setup.glossary,
        icon: ICONS.blank,
      });
    }

    if (docsNotification) {
      items.push({
        title: 'Notifications Help',
        path: paths.docs.setup.notifications,
        icon: ICONS.blank,
      });
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    docsSetupApproval,
    docsOrgCredentials,
    docsTranslationEngine,
    docsGlossary,
    docsNotification,
  ]);

  const docsTranslationWorkbenchItems = useMemo(() => {
    const items = [];
    if (docsRetrieveMetadata) {
      items.push({
        title: 'Retrieve Metadata Guide',
        path: paths.docs.translationWorkbench.retrieveMetadata,
        icon: ICONS.blank,
      });
    }

    if (docsFilterMetadata) {
      items.push({
        title: 'Filter Metadata Guide',
        path: paths.docs.translationWorkbench.filterMetadata,
        icon: ICONS.blank,
      });
    }

    if (docsTranslateMetadata) {
      items.push({
        title: 'Translate Metadata Help',
        path: paths.docs.translationWorkbench.translateMetadata,
        icon: ICONS.blank,
      });
    }

    if (docsDeployMetadata) {
      items.push({
        title: 'Deploy Metadata Guide',
        path: paths.docs.translationWorkbench.deployMetadata,
        icon: ICONS.blank,
      });
    }

    if (docsLimit) {
      items.push({
        title: 'Limit Blog',
        path: paths.docs.translationWorkbench.limit,
        icon: ICONS.blank,
      });
    }

    if (docsTokenHistory) {
      items.push({
        title: 'Token History Guide',
        path: paths.docs.translationWorkbench.limit,
        icon: ICONS.blank,
      });
    }

    return items;
  }, [
    docsRetrieveMetadata,
    docsFilterMetadata,
    docsTranslateMetadata,
    docsDeployMetadata,
    docsLimit,
    docsTokenHistory,
  ]);

  const docsMTJobItems = useMemo(() => {
    const items = [];
    if (docsCreateUpdateJob) {
      items.push({
        title: 'Create/Update Job Guide',
        path: paths.docs.MTJob.createUpdateJob,
        icon: ICONS.blank,
      });
    }
    if (docsMTJobStatus) {
      items.push({
        title: 'Meta Translate Job Status Help',
        path: paths.docs.MTJob.status,
        icon: ICONS.blank,
      });
    }
    if (docsCompareOrgs) {
      items.push({
        title: 'Compare Orgs Help',
        path: paths.docs.MTJob.compareOrgs,
        icon: ICONS.blank,
      });
    }

    if (docsOrgsDeployment) {
      items.push({
        title: 'Orgs Deployment Guide',
        path: paths.docs.MTJob.orgsDeployment,
        icon: ICONS.blank,
      });
    }
    return items;
  }, [docsCreateUpdateJob, docsMTJobStatus, docsCompareOrgs, docsOrgsDeployment]);

  const docsSalesforceItems = useMemo(() => {
    const items = [];

    if (docsInstallAPackage) {
      items.push({
        title: 'Install a Package Blog',
        path: paths.docs.salesforce.installPackage,
        icon: ICONS.blank,
      });
    }
    if (docsAuthenticationSalesforce) {
      items.push({
        title: 'Authentication and Setup Blog',
        path: paths.docs.salesforce.authenticationSalesforce,
        icon: ICONS.blank,
      });
    }
    if (docsDetectLanguage) {
      items.push({
        title: 'Language Detection Blog',
        path: paths.docs.salesforce.detectLanguage,
        icon: ICONS.blank,
      });
    }
    if (docsTranslate) {
      items.push({
        title: 'RealTime Data Translation Blog',
        path: paths.docs.salesforce.translate,
        icon: ICONS.blank,
      });
    }
    if (docsEmailAI) {
      items.push({
        title: 'Email Translation Blog',
        path: paths.docs.salesforce.emailAI,
        icon: ICONS.blank,
      });
    }
    if (docsEditRecordPage) {
      items.push({
        title: 'Edit Record Page Blog',
        path: paths.docs.salesforce.editRecordPage,
        icon: ICONS.blank,
      });
    }
    if (docsCaseCommentAI) {
      items.push({
        title: 'Case Comment Blog',
        path: paths.docs.salesforce.caseCommentAI,
        icon: ICONS.blank,
      });
    }
    if (docsRecordAI) {
      items.push({
        title: 'Record AI Blog',
        path: paths.docs.salesforce.recordAI,
        icon: ICONS.blank,
      });
    }
    if (docsFileAI) {
      items.push({
        title: 'File AI Blog',
        path: paths.docs.salesforce.fileAI,
        icon: ICONS.blank,
      });
    }
    if (docsSTCiCdComponent) {
      items.push({
        title: 'ST CI/CD Component',
        path: paths.docs.salesforce.stCiCdComponent,
        icon: ICONS.blog,
      });
    }

    if (docsAIService) {
      items.push({
        title: 'AI Service',
        path: paths.docs.salesforce.aiService,
        icon: ICONS.blog,
      });
    }

    if (docsFlowSTAction) {
      items.push({
        title: 'Flow ST Action',
        path: paths.docs.salesforce.flowSTConfiguration,
        icon: ICONS.blog,
      });
    }

    if (docsJsonParser) {
      items.push({
        title: 'JSON Parser',
        path: paths.docs.salesforce.stJsonParser,
        icon: ICONS.blog,
      });
    }

    if (docsAsyncResponseCheck) {
      items.push({
        title: 'Async Response Check',
        path: paths.docs.salesforce.aiAsyncResponseCheck,
        icon: ICONS.blog,
      });
    }
        
    if (docsN8nSetup) {
      items.push({
        title: 'n8n Setup',
        path: paths.docs.salesforce.n8nSetup,
        icon: ICONS.blog,
      });
    }

    if (docsChatbotAIAgent) {
      items.push({
        title: 'SimpleT AI Agent',
        path: paths.docs.salesforce.chatbotAIAgent,
        icon: ICONS.blog,
      });
    }

    if (docsFeedback) {
      items.push({
        title: 'SimpleT Feedback',
        path: paths.docs.salesforce.feedback,
        icon: ICONS.blog,
      });
    }

    return items;
  }, [
    docsChatbotAIAgent,
    docsFeedback,
    docsDetectLanguage,
    docsTranslate,
    docsAuthenticationSalesforce,
    docsInstallAPackage,
    docsEmailAI,
    docsEditRecordPage,
    docsCaseCommentAI,
    docsRecordAI,
    docsFileAI,
    docsSTCiCdComponent,
    docsAIService,
    docsFlowSTAction,
    docsJsonParser,
    docsAsyncResponseCheck,
    docsN8nSetup,
  ]);

  const docsOpenAPIsItems = useMemo(() => {
    const items = [];

    if (docsToken) {
      items.push({
        title: 'Token Guide',
        path: paths.docs.openAPIs.token,
        icon: ICONS.blank,
      });
    }

    return items;
  }, [docsToken]);

  const docsSimpleTAIItems = useMemo(() => {
    const items = [];

    // Use AI Service permission for SimpleT AI docs
    if (docsAIService) {
      items.push({
        title: 'LLM Manager',
        path: paths.docs.simpleTAI.llmManagerGuide,
        icon: ICONS.invoice,
      });
      items.push({
        title: 'Prompt Builder',
        path: paths.docs.simpleTAI.promptBuilderGuide,
        icon: ICONS.analytics,
      });
      items.push({
        title: 'AI Command Center',
        path: paths.docs.simpleTAI.aiCommandCenterGuide,
        icon: ICONS.dashboard,
      });
    }

    return items;
  }, [docsAIService]);

  const docsBillingItems = useMemo(() => {
    const items = [];

    if (docsBilling) {
      items.push({
        title: 'Billing & Subscription Guide',
        path: paths.docs.billing.guide,
        icon: ICONS.billing,
      });
    }

    return items;
  }, [docsBilling]);

  const supportItems = useMemo(() => {
    const items = [];

    if (allowedFaqs) {
      items.push({
        title: 'Faqs',
        path: paths.supportPath.faqs,
        icon: ICONS.faqs,
      });
    }

    if (allowedContact) {
      items.push({
        title: 'Contact',
        path: paths.supportPath.contact,
        icon: ICONS.support,
      });
    }

    return items;
  }, [allowedFaqs, allowedContact]);

  const data = useMemo(
    () => [
      // DOCUMENTATION
      // ----------------------------------------------------------------------
      {
        subheader: 'User Docs',
        items: docsUserItems,
      },

      {
        subheader: 'Setup Docs',
        items: docsSetupItems,
      },

      {
        subheader: 'Translation Workbench Docs',
        items: docsTranslationWorkbenchItems,
      },

      {
        subheader: 'Metadata Translate Job Docs',
        items: docsMTJobItems,
      },

      {
        subheader: 'Salesforce Docs',
        items: docsSalesforceItems,
      },

      {
        subheader: 'Open APIs Docs',
        items: docsOpenAPIsItems,
      },

      {
        subheader: 'SimpleT AI Docs',
        items: docsSimpleTAIItems,
      },

      {
        subheader: 'Billing Docs',
        items: docsBillingItems,
      },

      // SUPPORT
      // ----------------------------------------------------------------------
      {
        subheader: 'Support',
        items: supportItems,
      },
    ],
    [
      docsUserItems,
      docsSetupItems,
      docsTranslationWorkbenchItems,
      docsMTJobItems,
      docsSalesforceItems,
      docsOpenAPIsItems,
      docsSimpleTAIItems,
      docsBillingItems,
      supportItems,
    ]
  );

  return data;
}
